import {StyleSheet} from "aphrodite";
import * as React from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import {usePrefersReducedMotion} from "@khan/data-prefers-reduced-motion";

import ActiveCard from "./active-card.tsx";
import BlankCard from "./blank-card.tsx";
import {containerDimensions} from "./card-sizing.ts";
import {CARD_OFFSET, CONTROLS_HEIGHT} from "./constants.ts";
import sharedStyles from "./shared-styles.ts";
import type {BlooketQuestion, DeckAnimation} from "./types.ts";

interface CardStackProps {
    activeCard: BlooketQuestion | null;
    setCard: (value: BlooketQuestion) => void;
    cardWidth: number;
    cardHeight: number;
    controlsCard: React.ReactNode;
    animateOut: BlooketQuestion | null;
    animation: DeckAnimation;
    isAnimating: boolean;
}

function getBottomOffset(position: number): number {
    return position * CARD_OFFSET + CONTROLS_HEIGHT;
}

function getRightOffset(position: number): number {
    return position * CARD_OFFSET;
}

function CardStack(props: CardStackProps): React.ReactNode {
    const {
        cardWidth: width,
        cardHeight: height,
        activeCard,
        setCard,
        controlsCard,
        animation,
        animateOut,
        isAnimating,
    } = props;
    const prefersReducedMotion = usePrefersReducedMotion();

    const topAnimation = () => {
        if (prefersReducedMotion) {
            // For users who prefer reduced motion,
            // we use the same simple fade out animation
            // for both animations.
            return sharedStyles.fadingOut;
        }

        switch (animation) {
            case "ACCEPTING":
                return sharedStyles.accepting;
            case "DISCARDING":
                return sharedStyles.discarding;
            default:
                return {};
        }
    };

    const animatingCards = [
        <BlankCard
            cardWidth={props.cardWidth}
            cardHeight={props.cardHeight}
            bottomOffset={getBottomOffset(-1)}
            rightOffset={getRightOffset(0)}
            animationStyle={sharedStyles.fadingIn}
            delay={0.6}
            key="incoming"
        />,
        <BlankCard
            cardWidth={props.cardWidth}
            cardHeight={props.cardHeight}
            bottomOffset={getBottomOffset(0)}
            rightOffset={getRightOffset(1)}
            animationStyle={sharedStyles.slidingUp}
            delay={0.4}
            key="middle-animated"
        />,
        <ActiveCard
            cardWidth={props.cardWidth}
            cardHeight={props.cardHeight}
            bottomOffset={getBottomOffset(1)}
            rightOffset={getRightOffset(2)}
            activeCard={activeCard}
            setCard={setCard}
            animationStyle={sharedStyles.slidingUp}
            delay={0.2}
            key="active"
        />,
        <ActiveCard
            cardWidth={props.cardWidth}
            cardHeight={props.cardHeight}
            bottomOffset={getBottomOffset(2)}
            rightOffset={getRightOffset(3)}
            activeCard={animateOut}
            setCard={() => {}}
            animationStyle={topAnimation()}
            delay={0}
            key="outgoing"
        />,
    ];
    const staticCards = [
        <BlankCard
            cardWidth={props.cardWidth}
            cardHeight={props.cardHeight}
            bottomOffset={getBottomOffset(0)}
            rightOffset={getRightOffset(1)}
            key="bottom"
        />,
        <BlankCard
            cardWidth={props.cardWidth}
            cardHeight={props.cardHeight}
            bottomOffset={getBottomOffset(1)}
            rightOffset={getRightOffset(2)}
            key="middle-static"
        />,
        <ActiveCard
            cardWidth={props.cardWidth}
            cardHeight={props.cardHeight}
            bottomOffset={getBottomOffset(2)}
            rightOffset={getRightOffset(3)}
            activeCard={activeCard}
            setCard={setCard}
            animationStyle={{}}
            delay={0}
            key="top"
        />,
    ];

    return (
        <View style={styles.container}>
            <View
                style={[styles.cardStack, containerDimensions(width, height)]}
            >
                {controlsCard}
                {isAnimating ? animatingCards : staticCards}
            </View>
        </View>
    );
}

const styles = StyleSheet.create({
    container: {
        position: "static",
        display: "flex",
        flexDirection: "row",
        alignItems: "end",
    },
    cardStack: {
        position: "relative",
        display: "block",
    },
});

export default CardStack;
